import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Star } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WatchlistContext = createContext({ symbols: [], isStarred: () => false, toggle: () => {}, version: 0 });

export const useWatchlist = () => useContext(WatchlistContext);

export const WatchlistProvider = ({ userId, children }) => {
  const [symbols, setSymbols] = useState([]);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (!userId) { setSymbols([]); return; }
    axios.get(`${API}/watchlist`).then((r) => setSymbols(r.data.symbols || [])).catch(() => {});
  }, [userId]);

  const isStarred = useCallback((s) => symbols.includes(s), [symbols]);

  const toggle = useCallback(async (symbol) => {
    const starred = symbols.includes(symbol);
    setSymbols((prev) => (starred ? prev.filter((s) => s !== symbol) : [symbol, ...prev]));
    try {
      if (starred) await axios.delete(`${API}/watchlist/${symbol}`);
      else await axios.post(`${API}/watchlist/${symbol}`);
      toast.success(starred ? `${symbol} left the corral` : `${symbol} added to your watchlist`);
      setVersion((v) => v + 1);
    } catch (e) {
      setSymbols((prev) => (starred ? [symbol, ...prev] : prev.filter((s) => s !== symbol)));
      toast.error(e.response?.data?.detail || "Couldn't update watchlist");
    }
  }, [symbols]);

  return <WatchlistContext.Provider value={{ symbols, isStarred, toggle, version }}>{children}</WatchlistContext.Provider>;
};

export const StarButton = ({ symbol, size = 18, className = "" }) => {
  const { isStarred, toggle } = useWatchlist();
  const on = isStarred(symbol);
  return (
    <button
      type="button"
      className={`star-btn ${on ? "on" : ""} ${className}`}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(symbol); }}
      onMouseDown={(e) => e.preventDefault()}
      aria-label={on ? `Remove ${symbol} from watchlist` : `Add ${symbol} to watchlist`}
      aria-pressed={on}
      data-testid={`star-btn-${symbol}`}
    >
      <Star size={size} fill={on ? "currentColor" : "none"} strokeWidth={2} />
    </button>
  );
};

const Sparkline = ({ points, up }) => {
  if (!points || points.length < 2) return <div className="sparkline empty" />;
  const min = Math.min(...points);
  const range = Math.max(...points) - min || 1;
  const d = points.map((p, i) => `${(i / (points.length - 1)) * 100},${30 - ((p - min) / range) * 28 - 1}`).join(" ");
  return (
    <svg className={`sparkline ${up ? "positive" : "negative"}`} viewBox="0 0 100 30" preserveAspectRatio="none" aria-hidden="true">
      <polyline points={d} fill="none" strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

const WatchRow = ({ stock }) => {
  const navigate = useNavigate();
  const up = (stock.change_percent || 0) >= 0;
  return (
    <div className="watch-row" onClick={() => navigate(`/stock/${stock.symbol}`)} data-testid={`watch-row-${stock.symbol}`}>
      <div className="watch-id">
        <span className="watch-symbol">{stock.symbol}</span>
        <span className="watch-name">{stock.name}</span>
      </div>
      <Sparkline points={stock.sparkline} up={stock.sparkline?.length > 1 ? stock.sparkline.at(-1) >= stock.sparkline[0] : up} />
      <div className="watch-quote">
        <span className="watch-price">{stock.price == null ? "—" : `$${stock.price.toFixed(2)}`}</span>
        {stock.price != null && <span className={`watch-change ${up ? "positive" : "negative"}`}>{up ? "+" : ""}{stock.change_percent.toFixed(2)}%</span>}
      </div>
      <StarButton symbol={stock.symbol} size={17} />
    </div>
  );
};

export const WatchlistCorral = () => {
  const { symbols, version } = useWatchlist();
  const navigate = useNavigate();
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/watchlist`)
      .then((r) => setStocks(r.data.stocks || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [version]);

  const visible = stocks.filter((s) => symbols.includes(s.symbol));

  return (
    <section className="watch-corral" data-testid="watchlist-corral">
      <div className="section-header">
        <h3 className="section-title">Watchlist Corral</h3>
        <span className="badge-count" data-testid="watchlist-count">{symbols.length} watching</span>
      </div>
      {loading ? (
        <div className="loading-spinner small" />
      ) : visible.length === 0 ? (
        <div className="watch-empty" data-testid="watchlist-empty">
          <p>No stocks in the corral yet. Tap the star on any stock to keep an eye on it.</p>
          <button className="pager-btn" onClick={() => navigate("/explore")} data-testid="watchlist-explore-btn">Find stocks</button>
        </div>
      ) : (
        <div className="watch-list">{visible.map((s) => <WatchRow key={s.symbol} stock={s} />)}</div>
      )}
    </section>
  );
};
